import React, { useState, createContext, useContext } from 'react';
type RecordingState = 'inactive' | 'recording' | 'paused';
type Recording = {
  id: string;
  title: string;
  duration: number; // in seconds
  date: Date;
  audioUrl?: string;
  customerId?: string;
  transcription?: string;
  analysis?: RecordingAnalysis;
};
type RecordingAnalysis = {
  customerSummary: string;
  keyRequirements: string[];
  destinations: string[];
  budget?: string;
  travelDates?: string;
  groupSize?: string;
  interests: string[];
  actionItems: string[];
};
interface RecordingContextType {
  recordingState: RecordingState;
  currentRecording: Recording | null;
  recordings: Recording[];
  recordingTime: number;
  startRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  stopRecording: () => void;
  saveRecording: (title: string, customerId?: string) => void;
  deleteRecording: (id: string) => void;
  getRecordingById: (id: string) => Recording | undefined;
}
const RecordingContext = createContext<RecordingContextType | undefined>(undefined);
export const RecordingProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [recordingState, setRecordingState] = useState<RecordingState>('inactive');
  const [currentRecording, setCurrentRecording] = useState<Recording | null>(null);
  const [recordings, setRecordings] = useState<Recording[]>([{
    id: 'rec-1',
    title: 'Johnson Family Vacation Planning',
    duration: 540,
    date: new Date(Date.now() - 86400000),
    transcription: 'Customer is looking for a family vacation to Europe in summer...',
    analysis: {
      customerSummary: 'Family of 4, budget $5000-$7000, interested in cultural experiences in Europe',
      keyRequirements: ['Family-friendly accommodations', 'Cultural activities', 'Some beach time'],
      destinations: ['Italy', 'Spain', 'France'],
      budget: '$5000-$7000',
      travelDates: 'June-August 2023',
      groupSize: '4',
      interests: ['History', 'Food', 'Beaches'],
      actionItems: ['Send Italy family package options', 'Check availability for June 15-30', 'Verify passport requirements']
    }
  }, {
    id: 'rec-2',
    title: 'Smith Honeymoon Discussion',
    duration: 720,
    date: new Date(Date.now() - 172800000),
    transcription: 'Couple discussing honeymoon options in the Caribbean or South Pacific...',
    analysis: {
      customerSummary: 'Newlywed couple, luxury budget $10000+, looking for romantic beach destination',
      keyRequirements: ['Luxury accommodations', 'Privacy', 'Beautiful beaches'],
      destinations: ['Maldives', 'Bora Bora', 'St. Lucia'],
      budget: '$10,000+',
      travelDates: 'September 2023',
      groupSize: '2',
      interests: ['Romance', 'Snorkeling', 'Spa'],
      actionItems: ['Send Maldives overwater bungalow options', 'Check honeymoon packages with spa credits', 'Verify COVID requirements']
    }
  }]);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingTimer, setRecordingTimer] = useState<NodeJS.Timeout | null>(null);
  const startRecording = () => {
    setRecordingState('recording');
    setRecordingTime(0);
    setCurrentRecording({
      id: `rec-${Date.now()}`,
      title: `Recording ${new Date().toLocaleString()}`,
      duration: 0,
      date: new Date()
    });
    const timer = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
    setRecordingTimer(timer as unknown as NodeJS.Timeout);
  };
  const pauseRecording = () => {
    if (recordingTimer) {
      clearInterval(recordingTimer);
      setRecordingTimer(null);
    }
    setRecordingState('paused');
  };
  const resumeRecording = () => {
    setRecordingState('recording');
    const timer = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
    setRecordingTimer(timer as unknown as NodeJS.Timeout);
  };
  const stopRecording = () => {
    if (recordingTimer) {
      clearInterval(recordingTimer);
      setRecordingTimer(null);
    }
    if (currentRecording) {
      setCurrentRecording({
        ...currentRecording,
        duration: recordingTime
      });
    }
    setRecordingState('inactive');
  };
  const saveRecording = (title: string, customerId?: string) => {
    if (!currentRecording) return;
    const updatedRecording: Recording = {
      ...currentRecording,
      title,
      customerId,
      // Simulate AI transcription and analysis
      transcription: 'This is a simulated transcription of the conversation...',
      analysis: {
        customerSummary: 'Family of 4, budget $3000-$5000, interested in beach destinations',
        keyRequirements: ['Family-friendly resort', 'Direct flights', 'All-inclusive options'],
        destinations: ['Mexico', 'Dominican Republic', 'Florida'],
        budget: '$3000-$5000',
        travelDates: 'December 2023',
        groupSize: '4',
        interests: ['Beaches', 'Swimming', 'Kid-friendly activities'],
        actionItems: ['Send family resort options in Cancun', 'Check flight availability', 'Verify passport requirements for children']
      }
    };
    setRecordings(prev => [updatedRecording, ...prev]);
    setCurrentRecording(null);
  };
  const deleteRecording = (id: string) => {
    setRecordings(prev => prev.filter(recording => recording.id !== id));
  };
  const getRecordingById = (id: string) => {
    return recordings.find(recording => recording.id === id);
  };
  return <RecordingContext.Provider value={{
    recordingState,
    currentRecording,
    recordings,
    recordingTime,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    saveRecording,
    deleteRecording,
    getRecordingById
  }} data-id="element-4285">
      {children}
    </RecordingContext.Provider>;
};
export const useRecording = () => {
  const context = useContext(RecordingContext);
  if (context === undefined) {
    throw new Error('useRecording must be used within a RecordingProvider');
  }
  return context;
};